import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { EventType, useAnalytics } from '../ui/Analytics';

interface Card {
  cardNumber: string;
  nameOnCard: string;
  customerId: string;
}

interface CardDropdownProps {
  availableCards: Card[];
  selectedCard: Card | null;
  setSelectedCard: React.Dispatch<React.SetStateAction<Card | null>>;
  className?: string;
  customStyle?: boolean;
  showAllCardholdersOption?: boolean;
}

const CardDropdown: React.FC<CardDropdownProps> = ({
  availableCards,
  selectedCard,
  setSelectedCard,
  className,
  customStyle,
  showAllCardholdersOption = false,
}) => {
  const { trackEvent } = useAnalytics();
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const { assets, entries } = contentfulData;
  const { rb_common } = entries;
  const [selectCardDropdownIsOpen, setSelectCardDropdownIsOpen] =
    useState(false);
  const selectCardRef = useRef<HTMLDivElement>(null);

  const formatCardDisplay = (card: Card | null) => {
    if (!card) return '';
    const lastFourDigits = card.cardNumber ? card.cardNumber.slice(-4) : '';
    return `${card.nameOnCard}...${lastFourDigits}`;
  };

  // useEffect(() => {
  //   trackEvent(
  //     EventType.PAGE_VIEW,
  //     ['earned cash back rewards - auth users', 'rewards'],
  //     window.location.href,
  //     undefined
  //   );
  // }, []);

  const trackPageAndClickEvent = (nameOnCard: string) => {
    trackEvent(
      EventType.CLICK_EVENT,
      ['earned cash back rewards - auth users', 'rewards'],
      window.location.href,
      `earned cash back rewards - auth users | ${nameOnCard}`
    );
  };

  const handleCardChange = (customerId: string) => {
    const customerIdSplit = customerId.split('_');

    const selectedCardOption = availableCards?.find(
      (card: any) =>
        card?.customerId === customerIdSplit[1] &&
        card?.nameOnCard === customerIdSplit[0]
    );

    if (selectedCardOption) {
      setSelectedCard(selectedCardOption);
      trackPageAndClickEvent(
        selectedCardOption?.nameOnCard === rb_common.commonString.allCardholders
          ? selectedCardOption?.nameOnCard
          : formatCardDisplay(selectedCardOption)
      );
    }
    setSelectCardDropdownIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectCardRef.current &&
        !selectCardRef.current.contains(event.target as Node)
      ) {
        setSelectCardDropdownIsOpen(false);
      }
    };
    document.body.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.body.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const cardsToDisplay = showAllCardholdersOption
    ? [
        ...(availableCards.some(
          (card) => card.nameOnCard === rb_common.commonString.allCardholders
        )
          ? []
          : [
              {
                nameOnCard: rb_common.commonString.allCardholders,
                cardNumber: '',
                customerId: '',
                ...(availableCards[0] && {
                  customerType: availableCards[0],
                }),
              },
            ]),
        ...availableCards,
      ]
    : availableCards;

  return (
    <div
      className={`relative ${className || 'md:w-[90%] mb-5'}`}
      ref={selectCardRef}
    >
      <div
        className={`w-full lg:mt-0 mt-3 flex justify-between pb-2 ${
          customStyle
            ? 'lg:mt-0 mt-3'
            : 'border-b-[1px] border-[#999999] text-[#1F1F1F] text-[16px] font-[650]'
        } leading-[24px] tracking-[-0.051px]`}
        onClick={() => setSelectCardDropdownIsOpen((prevState) => !prevState)}
        role="button"
        data-testid="dropdown-header"
      >
        <div>
          {selectedCard?.nameOnCard === rb_common.commonString.allCardholders
            ? selectedCard?.nameOnCard
            : formatCardDisplay(selectedCard)}
        </div>
        <img src={assets?.down_arrow} alt="downArrow" className="w-6 h-6" />
      </div>
      <div
        className={`absolute top-[40px] max-h-44 overflow-y-auto z-40 w-full overflow-auto rounded-[15px] bg-white py-1 text-base focus:outline-none lg:mt-1 md:mt-3 mt-1 ${
          selectCardDropdownIsOpen ? 'block' : 'hidden'
        } shadow-lg`}
        role="list"
      >
        {cardsToDisplay.map((card) => (
          <div
            key={card?.nameOnCard + '_' + card?.customerId}
            className="flex justify-between w-full text-[#000] text-[16px] font-[600] leading-[20px] p-[15px] hover:bg-[#f5f5f5] cursor-pointer"
            onClick={() =>
              handleCardChange(card?.nameOnCard + '_' + card?.customerId)
            }
            role="listitem"
          >
            {card.nameOnCard === rb_common.commonString.allCardholders
              ? card.nameOnCard
              : formatCardDisplay(card)}
            {selectedCard?.nameOnCard + '_' + selectedCard?.customerId ===
              card?.nameOnCard + '_' + card?.customerId && (
              <img
                src={assets?.check_mark}
                alt="checkIcon"
                className="w-6 h-6"
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CardDropdown;
